import * as i0 from '@angular/core';
import { signal, Injectable } from '@angular/core';
import { Subject, BehaviorSubject, fromEvent } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

const isChrome = (win) => testUserAgent(win, /Chrome/i);
const isFirefox = (win) => testUserAgent(win, /Firefox/i);
const isEdge = (win) => testUserAgent(win, /Edge/i);
const isSafari = (win) => testUserAgent(win, /Safari/i);
const isOpera = (win) => testUserAgent(win, /Opera/i) || testUserAgent(win, /OPR/i);
const isMSIE = (win) => testUserAgent(win, /MSIE/i) || testUserAgent(win, /Trident/i);
const isMobileWeb = (win) => isMobile(win) && !isHybrid(win);
const isIpad = (win) => {
    // iOS 12 and below
    if (testUserAgent(win, /iPad/i)) {
        return true;
    }
    // iOS 13+
    if (testUserAgent(win, /Macintosh/i) && isMobile(win)) {
        return true;
    }
    return false;
};
const isIphone = (win) => testUserAgent(win, /iPhone/i);
const isIOS = (win) => testUserAgent(win, /iPhone|iPod/i) || isIpad(win);
const isAndroid = (win) => testUserAgent(win, /android|sink/i);
const isAndroidTablet = (win) => {
    return isAndroid(win) && !testUserAgent(win, /mobile/i);
};
const isPhablet = (win) => {
    const width = win.innerWidth;
    const height = win.innerHeight;
    const smallest = Math.min(width, height);
    const largest = Math.max(width, height);
    return smallest > 390 && smallest < 520 && largest > 620 && largest < 800;
};
const isTablet = (win) => {
    const width = win.innerWidth;
    const height = win.innerHeight;
    const smallest = Math.min(width, height);
    const largest = Math.max(width, height);
    return isIpad(win) || isAndroidTablet(win) || (smallest > 460 && smallest < 820 && largest > 780 && largest < 1400);
};
const isMobile = (win) => matchMedia(win, '(any-pointer:coarse)');
const isTouch = (win) => matchMedia(win, '(any-pointer:coarse)');
const isDesktop = (win) => !isMobile(win);
const isHybrid = (win) => isCordova(win) || isCapacitorNative(win);
const isCordova = (win) => !!(win['cordova'] || win['phonegap'] || win['PhoneGap']);
const isCapacitorNative = (win) => {
    const capacitor = win['Capacitor'];
    return !!(capacitor && capacitor.isNative);
};
const isElectron = (win) => testUserAgent(win, /electron/i);
const isPWA = (win) => !!(win.matchMedia('(display-mode: standalone)').matches || win.navigator.standalone);
const testUserAgent = (win, expr) => expr.test(win.navigator.userAgent);
const isBrowser = new Function('try {return this===window;}catch(e){ return false;}');
const isServer = new Function('try {return this===global;}catch(e){return false;}');
const matchMedia = (win, query) => {
    return win.matchMedia(query).matches;
};
const isSMScreen = (win) => matchMedia(win, '(max-width: 578px)');
const isMDScreen = (win) => matchMedia(win, '(max-width: 768px)');
const isLGScreen = (win) => matchMedia(win, '(max-width: 992px)');
const isXLScreen = (win) => matchMedia(win, '(max-width: 1200px)');
const is2XLScreen = (win) => matchMedia(win, '(max-width: 1440px)');
const is3XLScreen = (win) => matchMedia(win, '(max-width: 1920px)');
const is4XLScreen = (win) => matchMedia(win, '(max-width: 2560px)');
const PLATFORMS_MAP = {
    Android: isAndroid,
    iOS: isIOS,
    Desktop: isDesktop,
    Mobile: isMobile,
    Touch: isTouch,
    Chrome: isChrome,
    Firefox: isFirefox,
    Safari: isSafari,
    Edge: isEdge,
    Opera: isOpera,
    Hybrid: isHybrid,
    PWA: isPWA,
    Electron: isElectron,
    SM: isSMScreen,
    MD: isMDScreen,
    LG: isLGScreen,
    XL: isXLScreen,
    '2XL': is2XLScreen,
    '3XL': is3XLScreen,
    '4XL': is4XLScreen,
};
class AXPlatformEvent {
}
class AXPlatform {
    isRtl() {
        return document.dir == 'rtl' || document.body.dir == 'rtl' || document.body.style.direction == 'rtl';
    }
    isLandscape() {
        return window.innerHeight < window.innerWidth;
    }
    isPortrate() {
        return !this.isLandscape();
    }
    is(name) {
        if (isBrowser()) {
            return PLATFORMS_MAP[name](window) || false;
        }
        else {
            return false;
        }
    }
    get screenSize() {
        return (['SM', 'MD', 'LG', 'XL', '2XL', '3XL', '4XL'].find((name) => PLATFORMS_MAP[name](window) == true) || '5XL');
    }
    switchLightMode() {
        const _html = document.getElementsByTagName('html')[0];
        _html.classList.remove('ax-dark');
        localStorage.setItem('AX_THEME_MODE', 'light');
        this.themeMode$.next('light');
        this.themeMode.set('light');
        return 'light';
    }
    switchDarkMode() {
        const _html = document.getElementsByTagName('html')[0];
        _html.classList.add('ax-dark');
        localStorage.setItem('AX_THEME_MODE', 'dark');
        this.themeMode$.next('dark');
        this.themeMode.set('dark');
        return 'dark';
    }
    switchSystemMode() {
        if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
            const _html = document.getElementsByTagName('html')[0];
            _html.classList.add('ax-dark');
        }
        else {
            const _html = document.getElementsByTagName('html')[0];
            _html.classList.remove('ax-dark');
        }
        localStorage.setItem('AX_THEME_MODE', 'system');
        this.themeMode$.next('system');
        this.themeMode.set('system');
    }
    autoDetectThemeMode() {
        const mode = localStorage.getItem('AX_THEME_MODE') || 'system';
        switch (mode) {
            case 'light':
                return this.switchLightMode();
            case 'dark':
                return this.switchDarkMode();
            case 'system':
                return this.switchSystemMode();
        }
    }
    _setFullHeightRatio() {
        if (isBrowser()) {
            try {
                document.querySelector(':root')?.style.setProperty('--ax-vh', window.innerHeight / 100 + 'px');
            }
            catch (error) {
                console.error(error);
            }
        }
    }
    autoSystemModeDetection(event) {
        if (this.themeMode() === 'system') {
            const themeMode = event.matches ? 'dark' : 'light';
            if (themeMode === 'dark') {
                const _html = document.getElementsByTagName('html')[0];
                _html.classList.add('ax-dark');
            }
            else {
                const _html = document.getElementsByTagName('html')[0];
                _html.classList.remove('ax-dark');
            }
        }
    }
    constructor() {
        this.resize = new Subject();
        this.click = new Subject();
        this.scroll = new Subject();
        this.themeMode$ = new BehaviorSubject('system');
        this.themeMode = signal('system');
        if (isBrowser()) {
            fromEvent(window, 'resize')
                .pipe(debounceTime(100))
                .pipe(distinctUntilChanged())
                .subscribe((e) => {
                this.resize.next({
                    nativeEvent: e,
                    source: this,
                });
                //
                this._setFullHeightRatio();
            });
            if (window?.matchMedia('(prefers-color-scheme: dark)').matches) {
                window?.matchMedia('(prefers-color-scheme: dark)')?.addEventListener('change', (event) => {
                    // Detect System color mode
                    this.autoSystemModeDetection(event);
                });
            }
            document.addEventListener('click', (e) => {
                this.click.next({
                    nativeEvent: e,
                    source: this,
                });
            }, true);
            document.addEventListener('scroll', (e) => {
                this.scroll.next({
                    nativeEvent: e,
                    source: this,
                });
            }, true);
            // init functions
            this._setFullHeightRatio();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXPlatform, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXPlatform, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXPlatform, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [] });

/**
 * Generated bundle index. Do not edit.
 */

export { AXPlatform, AXPlatformEvent, isBrowser, isServer, testUserAgent };
